import { QueryClient } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { getSession } from 'next-auth/react';
import { useEffect } from 'react';

import MainLoader from '../../components/Loader';
import useRouterMatch from '../../hooks/useRouterMatch';
import useSignUp from '../../hooks/useSignup';
import { detectAndBestEffortSetLanguage } from '../../utils/localisationUtils';
import { dehydrateReactQueryPrefetchedInfiniteQuery } from '../../utils/react-query-utils';

export default function Signup() {
  const router = useRouter();
  const { signUp } = useSignUp();
  const { tenantId } = useRouterMatch();

  useEffect(() => {
    // Always skip the signup form and go directly to Azure B2C
    const callbackUrl =
      (router.query.callbackUrl as string) || (router.query.callback as string);
    const finalCallbackUrl = callbackUrl || `/${tenantId}/create`;

    //Empty request to be backwards compatible with old signup flow
    signUp({
      callbackUrl: finalCallbackUrl,
      authParams: {
        ui_locales: 'en',
        accountType: 'teacher',
        tenantName: '',
        schoolRole: '',
        subjects: '',
        grades: '',
        primaryUse: '',
        otherRole: '',
        otherSubjects: '',
        otherGrades: '',
        otherPrimaryUse: '',
        v3Properties: JSON.stringify({
          organizationId: undefined,
          organizationGeoLocation: {
            country: '',
            region: '',
            city: '',
          },
          intendedUses: '',
          signUpOrigin: '',
          otherIntendedUse: '',
          otherSignUpOrigin: '',
        }),
      },
    });
  }, [signUp, router.query.callbackUrl]);

  // Show loading state while redirecting to Azure B2C
  return <MainLoader />;
}

export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=300, stale-while-revalidate=3600',
  );

  const session = await getSession({ req: context.req });
  const queryClient = new QueryClient();

  return {
    props: {
      dehydratedState: dehydrateReactQueryPrefetchedInfiniteQuery(queryClient),
      ...(await detectAndBestEffortSetLanguage(context)),
      ...(session && { session }),
    },
  };
};
